import React from 'react';
import { Autocomplete, AutocompleteItem, DatePicker, DateValue, Input, Button as NUIButton, Select, Selection, SelectItem } from '@nextui-org/react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router';
import { Calculation, CalculationWithCaregiverAndFamilyData } from '../../../types/calculation';
import { loadCalculationHistory } from '../../../service/calculation';
import { useSelector } from 'react-redux';
import { StoreState } from '../../../store/reducer-main';
import CalculationsHistoryTable from '../../components/tables/CalculationsHistoryTable';
import PageLayout from '../../components/common/PageLayout';
import theme from '../../theme';
import Typography from '../../components/common/Typography';
import Button from '../../components/common/Button';
import { parseDate } from '@internationalized/date';
import { CALCULATION_RESULT_SELECTOR_DATA, CalculationHistoryTableFilters, CalculationStringResult } from '../../../types/filters';
import { Family } from '../../../types/families';
import { CareGiver } from '../../../types/caregivers';
import { calendarDate2Timestamp, createPdfToExport, ts2Age } from '../../../helpers/utils';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { initFamiliesSearchResults, searchFamilyByName } from '../../../service/families';
import { initCaregiverSearchResults, searchCaregiverByName } from '../../../service/caregiovers';
import { deleteCalculationRecord, getCalculationHistoryMaxPageNumber, initCalculationHistoryTable, integrateCalculationWithFamilyAndCaregiverData, loadCalculationHistoryNextTablePage, loadCalculationHistoryPreviousTablePage } from '../../../service/calculationHistory';
import { DEFAULT_TABLE_PAGINATION } from '../../../constants/pagination';
import { TablePaginationConfig } from '../../../types/common';
import { toast } from 'react-toastify';


export default function CalculationHistoryPage() {

  const navigate = useNavigate();
  const { userProfile } = useSelector((state: StoreState) => state.profile);

  const [loading, setLoading] = React.useState<boolean>(false);
  const [loadingDownload, setLoadingDownload] = React.useState<boolean>(false);
  // paginazione
  const [history, setHistory] = React.useState<CalculationWithCaregiverAndFamilyData[]>([]);
  const [paginationConfig, setPaginationConfig] = React.useState<TablePaginationConfig>(DEFAULT_TABLE_PAGINATION);
  const [totalNumberOfPages, setTotalNumberOfPages] = React.useState<number>(1);
  // filters
  const [fromDate, setFromDate] = React.useState<DateValue | null>(null);
  const [toDate, setToDate] = React.useState<DateValue | null>(null);
  const [result, setResult] = React.useState<Selection>(new Set([]));
  const [familyFilter, setFamilyFilter] = React.useState<string | null>(null);
  const [caregiverFilter, setCaregiverFilter] = React.useState<string | null>(null);
  // search fields
  const [familySearchValue, setFamilySearchValue] = React.useState<string>('');
  const [caregiverSearchValue, setCaregiverSearchValue] = React.useState<string>('');
  const [familiesSearchResults, setFamiliesSearchResults] = React.useState<Family[]>([]);
  const [caregiverSearchResults, setCaregiverSearchResults] = React.useState<CareGiver[]>([]);

  React.useEffect(() => {
    init();
  }, []);

  const init = async () => {
    if (!userProfile) return;
    // init search values
    setLoading(true);
    const familiesInitialResults = await initFamiliesSearchResults(userProfile.uid);
    const caregiversInitialResults = await initCaregiverSearchResults(userProfile.uid);
    // paginazione
    const page = await initCalculationHistoryTable(userProfile.uid);
    const newCalculationHistoryPage = await integrateCalculationWithFamilyAndCaregiverData(userProfile.uid, page.data);
    const nop = await getCalculationHistoryMaxPageNumber(userProfile.uid, DEFAULT_TABLE_PAGINATION.size); // number of pages
    // stato
    setCaregiverSearchResults(caregiversInitialResults);  
    setFamiliesSearchResults(familiesInitialResults);
    setPaginationConfig({ page: 1, maxPage: nop, size: DEFAULT_TABLE_PAGINATION.size, firstDoc: page.cursors.first, lastDoc: page.cursors.last, secondDoc: page.cursors.second });
    setHistory(newCalculationHistoryPage);
    setTotalNumberOfPages(nop);
    setLoading(false);
  }

  const fetchNextPage = async () => {
    if (!userProfile) return; 
    setLoading(true);
    const { page, size, lastDoc, maxPage } = paginationConfig;
    if (!lastDoc) throw 'Invalid last doc';
    const filters = constructTableFilters();
    const nextPagePaginationConfig: TablePaginationConfig = { page: page + 1, size, lastDoc, maxPage };
    const nextPage = await loadCalculationHistoryNextTablePage(userProfile.uid, nextPagePaginationConfig, filters);
    const newPage = await integrateCalculationWithFamilyAndCaregiverData(userProfile.uid, nextPage.data);
    setPaginationConfig({ page: nextPagePaginationConfig.page, size, firstDoc: nextPage.cursors.first, secondDoc: nextPage.cursors.second, lastDoc: nextPage.cursors.last, maxPage });
    setHistory(newPage);
    setLoading(false);
  }

  const fetchPrevPage = async () => {
    if (!userProfile) return;
    setLoading(true);
    const { page, size, firstDoc, maxPage } = paginationConfig;
    if (!firstDoc) return 'Invalid first doc';
    const filters = constructTableFilters();
    const prevPaaginationConfig: TablePaginationConfig = { page: page - 1, size, firstDoc, maxPage };
    const prevPage = await loadCalculationHistoryPreviousTablePage(userProfile.uid, prevPaaginationConfig, filters);
    const newPage = await integrateCalculationWithFamilyAndCaregiverData(userProfile.uid, prevPage.data);
    setPaginationConfig({ page: prevPaaginationConfig.page, size, firstDoc: prevPage.cursors.first, secondDoc: prevPage.cursors.second, lastDoc: prevPage.cursors.last, maxPage });
    setHistory(newPage);
    setLoading(false);
  }

  const onFamilySearchValueChange = async (
    value: string
  ) => {
    if (!userProfile) return;
    setLoading(true);
    setFamilySearchValue(value);
    if (value.length === 0) {
      const familiesInitialResults = await initFamiliesSearchResults(userProfile.uid);
      setFamiliesSearchResults(familiesInitialResults);
    } else {
      const searchRes = await searchFamilyByName(userProfile.uid, value);
      setFamiliesSearchResults(searchRes);
    }
    setLoading(false);
  }

  const onCaregiverSearchValueChange = async (
    value: string
  ) => {
    if (!userProfile) return;
    setLoading(true);
    setCaregiverSearchValue(value);
    if (value.length === 0) {
      const caregiversInitialResults = await initCaregiverSearchResults(userProfile.uid);
      setCaregiverSearchResults(caregiversInitialResults);  
    } else {
      const searchRes = await searchCaregiverByName(userProfile.uid, value);
      setCaregiverSearchResults(searchRes);
    }
    setLoading(false);
  }

  const constructTableFilters = (): CalculationHistoryTableFilters => {
    const fromTs = fromDate ? calendarDate2Timestamp({day: fromDate.day, month: fromDate.month, year: fromDate.year}) : null;
    const toTs = toDate ? calendarDate2Timestamp({day: toDate.day, month: toDate.month, year: toDate.year}) : null;
    const stringRes = Array.from(result).length > 0 ? Array.from(result)[0] as CalculationStringResult : null;
    return {
      caregiver: caregiverFilter,
      family: familyFilter,
      from: fromTs,
      result: stringRes,
      to: toTs,
    }
  }

  const onFamilySelect = (
    familyId: string
  ) => {
    const familyIdx = familiesSearchResults.findIndex(el => el.id === familyId);
    if (familyIdx >= 0) {
      const newSelectedFamily = familiesSearchResults[familyIdx];
      setFamilySearchValue(`${newSelectedFamily.personalData.name} ${newSelectedFamily.personalData.surname}`);
      setFamilyFilter(newSelectedFamily.id);
    } else {
      onFamilySearchValueChange('')
      setFamilyFilter(null);
    }
  }

  const onCaregiverSelect = (
    caregiverId: string
  ) => {
    const caregiverIdx = caregiverSearchResults.findIndex(el => el.id === caregiverId);
    if (caregiverIdx >= 0) {
      const newSelectedCaregiver = caregiverSearchResults[caregiverIdx];
      setCaregiverSearchValue(`${newSelectedCaregiver.personalData.name} ${newSelectedCaregiver.personalData.surname}`);
      setCaregiverFilter(newSelectedCaregiver.id);
    } else {
      onCaregiverSearchValueChange('');
      setCaregiverFilter(null);
    }
  }

  const applyTableFilters = async () => {
    if (!userProfile) return;
    setLoading(true);
    const tableFilters = constructTableFilters();
    const page = await loadCalculationHistoryNextTablePage(userProfile.uid, DEFAULT_TABLE_PAGINATION, tableFilters);
    const maxPageNumber = await getCalculationHistoryMaxPageNumber(userProfile.uid, DEFAULT_TABLE_PAGINATION.size, tableFilters);
    const newPage = await integrateCalculationWithFamilyAndCaregiverData(userProfile.uid, page.data);
    setPaginationConfig({ page: 1, maxPage: maxPageNumber, size: DEFAULT_TABLE_PAGINATION.size, firstDoc: page.cursors.first, secondDoc: page.cursors.second, lastDoc: page.cursors.last });
    setTotalNumberOfPages(maxPageNumber);
    setHistory(newPage);
    setLoading(false);
  }

  const onDownloadPdf = async (
    calc: CalculationWithCaregiverAndFamilyData
  ) => {
    try {
      if (!userProfile) return;
      setLoadingDownload(true);
      await createPdfToExport(userProfile.uid, calc);
      setLoadingDownload(false);
    } catch (error) {
      setLoadingDownload(false);
      console.log('err download', error);
      toast.error('Si è verificato un errore durante il downlaod del report');
    }
  }

  const discardFilters = () => {
    setCaregiverFilter(null);
    setFamilyFilter(null);
    setFromDate(null);
    setToDate(null);
    setFamilySearchValue('');
    setCaregiverSearchValue('');
    setResult(new Set([]));
    init();
  }

  const onDelete = async (
    id: string
  ) => {
    try {
      if (!userProfile) return;
      await deleteCalculationRecord(userProfile.uid, id);
      toast.success('Calcolo eliminato con successo');
      init();
    } catch (error) {
      console.log('err', error);
      toast.error('Si è verificato un errore');
    }
  }

  const onLoadCalculation = (
    calculation: CalculationWithCaregiverAndFamilyData
  ) => {
    navigate('/interview', {
      state: { calculation }
    });
  }

  return (
    <PageLayout nestedPage>
      <div className='flex grow-1 gap-4 items-center'>
        <NUIButton isIconOnly aria-label="back" color='primary' size='sm' onClick={() => navigate(-1)} radius='full'>
          <ArrowBackIcon />
        </NUIButton>
        <p className='text-xl font-medium'>STORICO COLLOQUI</p>
      </div>
      <div className='flex flex-col w-full h-full mt-6 p-4' style={{
        borderRadius: theme.sizing.x2,
        backgroundColor: theme.color.background,
        border: '1.5px solid lightgray'
      }}>
        <Typography variant='button' style={{fontWeight: 600}}>Filtri</Typography>
        <div className='flex flex-wrap gap-8 items-center my-4'>
          <DatePicker
            className="flex-1 min-w-[160px]"
            label="Da"
            value={fromDate}
            onChange={setFromDate}
          />
          <DatePicker
            className="flex-1 min-w-[160px]"
            label="A"
            value={toDate}
            onChange={setToDate}
          />
          <Autocomplete
            className='flex-1 min-w-[250px]'
            placeholder='Famiglia'
            aria-label='Famiglia'
            items={familiesSearchResults}
            inputValue={familySearchValue}
            selectedKey={familyFilter}
            onSelectionChange={(id) => onFamilySelect(id as string)}
            onValueChange={onFamilySearchValueChange}>
              {
                (family) => (
                  <AutocompleteItem
                    key={family.id}>
                      <div className='flex flex-1 justify-between items-center'>
                        <div>
                          <Typography variant='label'>{family.personalData.name} {family.personalData.surname}</Typography>
                          <Typography variant='label' colorVariant='textLabel'>{family.personalData.address}</Typography>
                        </div>
                        <Typography variant='label' colorVariant='textLabel'>
                          {ts2Age(family.personalData.tsDateOfBirth).toString()} Anni
                        </Typography>
                      </div>
                  </AutocompleteItem>
                )
              }
          </Autocomplete>
          <Autocomplete
            className='flex-1 min-w-[250px]'
            placeholder='Badante'
            aria-label='Badante'
            items={caregiverSearchResults}
            inputValue={caregiverSearchValue}
            selectedKey={caregiverFilter}
            onSelectionChange={(id) => onCaregiverSelect(id as string)}
            onValueChange={onCaregiverSearchValueChange}>
              {
                (caregiver) => (
                  <AutocompleteItem
                    key={caregiver.id}>
                      <div className='flex flex-1 justify-between items-center'>
                        <div>
                          <Typography variant='label'>{caregiver.personalData.name} {caregiver.personalData.surname}</Typography>
                          <Typography variant='label' colorVariant='textLabel'>{caregiver.personalData.address}</Typography>
                        </div>
                        <Typography variant='label' colorVariant='textLabel'>
                          {ts2Age(caregiver.personalData.tsDateOfBirth).toString()} Anni
                        </Typography>
                      </div>
                  </AutocompleteItem>
                )
              }
          </Autocomplete>
          <Select
            label='Esito'
            className='flex-1 min-w-[150px]'
            selectionMode='single'
            selectedKeys={result}
            onSelectionChange={setResult}>
              {
                CALCULATION_RESULT_SELECTOR_DATA.map(r => (
                  <SelectItem key={r.key}>
                    {r.value}
                  </SelectItem>
                ))
              }
          </Select>
          <div className='flex flex-row gap-4 itmes-center'>
            <Button title='Applica' variant='primary' onClick={applyTableFilters} disabled={loading} />
            <Button title='Cancella' variant='secondary' onClick={discardFilters} disabled={loading} />
          </div>
        </div>
        <CalculationsHistoryTable
          loadingPdf={loadingDownload} 
          onLoadCalculation={onLoadCalculation}
          onDelete={onDelete}
          onDownload={onDownloadPdf}
          data={history} />
        <div className='flex justify-end gap-4 items-center mt-auto'>
          <NUIButton isIconOnly size='sm' onClick={fetchPrevPage} isDisabled={paginationConfig.page === 1 || loading}>
            <ArrowBackIosNewIcon fontSize='small'/>
          </NUIButton>
          <p>{paginationConfig.page}</p>
          <NUIButton 
            isIconOnly size='sm' 
            onClick={fetchNextPage} 
            isDisabled={paginationConfig.page >= totalNumberOfPages || loading}>
            <ArrowForwardIosIcon fontSize='small'/>
          </NUIButton>
        </div>
      </div>
    </PageLayout>
  )
}